/**
 * MatrixLegend — the key to the marks the matrix puts on a value.
 *
 * IT KEYS ONLY THE MARKS WE ACTUALLY DRAW (19 Aug 2026, Mike). That rule was applied when
 * this was written and then went stale, because it was applied ONCE, by hand: the list was
 * correct for the monthly matrix on the day and wrong for everything else after it.
 *
 * Measured 23 Aug 2026 against the mirror, both cadences:
 *   monthly   415 locked · 31 restated · 152 dq chips · 16 null values
 *   weekly      0 locked ·  0 restated · 270 dq chips ·  0 null values
 *
 * So on the weekly matrix every line in the old key described a mark that CANNOT appear —
 * weekly figures never lock, so there is no padlock and nothing to restate — while the one
 * mark on 270 cells went unnamed. A key that explains absent marks and omits present ones
 * is worse than no key: it is confidently wrong in a document a board reads.
 *
 * The fix is to apply the rule at RENDER time. Each line appears when the served envelope
 * actually carries that mark, so the key cannot drift from the grid again.
 *
 * DQ IS KEYED NOW; KNOWN-WRONG IS STILL NOT. The 19 Aug reasoning holds for the loud chip
 * and only for it — a cell that reads KNOWN-WRONG has named itself. "DQ" is two letters
 * that name nothing, and it is the most common mark on the page.
 *
 * The em-dash line was dropped. A dash where a number belongs reads as "no value" without
 * being told, and it was the one line whose removal costs a reader nothing.
 */
import React from 'react'
import type { Cell, ResolveResponse, Site } from '../../types/metrics'

interface LegendMark {
  id: string
  text: string
  tone?: string
}

const MARKS: Array<LegendMark & { present: (cells: Cell[]) => boolean }> = [
  {
    id: 'locked',
    text: '🔒 locked · the value froze 3 months after the month ended',
    present: (cells) => cells.some((cell) => cell.lock_state === 'locked'),
  },
  {
    id: 'forecast',
    text: '~ forecast · the whole month, projected — this month has not closed',
    tone: 'text-amber',
    present: (cells) => cells.some((cell) => cell.part_period && cell.forecast),
  },
  {
    id: 'to-date',
    text: '· to date · only what has posted so far, so cost lags revenue in a running month',
    present: (cells) => cells.some((cell) => cell.part_period && !cell.forecast),
  },
  {
    id: 'restated',
    text: '▲ restated · the value changed after it froze',
    tone: 'text-amber',
    present: (cells) => cells.some((cell) => cell.lock_state === 'divergent'),
  },
  {
    id: 'dq',
    text: 'DQ · a caveat rides on this value — point at the chip to read it',
    present: (cells) => cells.some((cell) => cell.dq_flags.length > 0),
  },
]

/** The marks present in the slice actually on screen — the site tab included, because a
 *  caveat scoped to Group is not on the page while Kirkstall is showing. Exported for the
 *  test: which lines appear is the behaviour, not the markup. */
export function legendMarks(resolve: ResolveResponse, site: Site): LegendMark[] {
  const visible = resolve.cells.filter((cell) => cell.site === site)
  return MARKS.filter((mark) => mark.present(visible)).map(({ id, text, tone }) => ({ id, text, tone }))
}

export default function MatrixLegend({
  resolve,
  site,
}: {
  resolve: ResolveResponse
  site: Site
}): React.JSX.Element | null {
  const marks = legendMarks(resolve, site)
  if (marks.length === 0) return null
  return (
    <div className="mt-3 flex flex-wrap gap-4 font-mono text-[10.5px] text-mut2">
      {marks.map((mark) => (
        <span key={mark.id} className={mark.tone}>
          {mark.text}
        </span>
      ))}
    </div>
  )
}
